import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Users, Search, Loader2, ChefHat, ChevronDown, ChevronRight, RefreshCw, Utensils, ArrowRight, ArrowLeft, CheckCircle2 } from 'lucide-react';
import api from '../services/api';
import { uyar, hata, onayla } from '../components/GlobalModal';

interface AvailableRow {
  id: number;
  employeeCode?: string;
  firstName?: string;
  lastName?: string;
  cardNumber?: string;
  departmentId?: number | null;
  departmentName?: string | null;
}
interface MealGroup {
  departmentId?: number | null;
  departmentName: string;
  count: number;
  employees: {
    id: number;
    employeeCode?: string;
    firstName?: string;
    lastName?: string;
    cardNumber?: string;
  }[];
}

const MealPersonnel: React.FC = () => {
  const [available, setAvailable] = useState<AvailableRow[]>([]);
  const [mealGroups, setMealGroups] = useState<MealGroup[]>([]);
  const [mealTotal, setMealTotal] = useState(0);
  const [depFilter, setDepFilter] = useState<number | ''>('');
  const [searchLeft, setSearchLeft] = useState('');
  const [searchRight, setSearchRight] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [rA, rB] = await Promise.all([
        api.get(`/meals/personnel/available${depFilter ? `?departmentId=${depFilter}` : ''}`),
        api.get('/meals/personnel'),
      ]);
      const rawA: any = (rA as any)?.data ?? rA;
      const arrA: AvailableRow[] = Array.isArray(rawA) ? rawA : (Array.isArray(rawA?.data) ? rawA.data : []);
      setAvailable(arrA);

      const rawB: any = (rB as any)?.data ?? rB;
      const inner = rawB?.data ?? rawB;
      const g = inner?.groups;
      setMealGroups(Array.isArray(g) ? g : []);
      setMealTotal(Number(inner?.total ?? 0));
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Personel listesi yüklenemedi');
    } finally { setLoading(false); }
  }, [depFilter]);

  useEffect(() => { load(); }, [load]);

  const departmentOptions = useMemo(() => {
    const map = new Map<number, string>();
    available.forEach(a => { if (a.departmentId != null) map.set(a.departmentId, a.departmentName ?? 'Belirsiz'); });
    mealGroups.forEach(g => { if (g.departmentId != null) map.set(g.departmentId, g.departmentName ?? 'Belirsiz'); });
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1], 'tr-TR'));
  }, [available, mealGroups]);

  const filteredAvailable = useMemo(() => {
    const q = searchLeft.trim().toLocaleLowerCase('tr-TR');
    if (!q) return available;
    return available.filter(a =>
      `${a.firstName ?? ''} ${a.lastName ?? ''}`.toLocaleLowerCase('tr-TR').includes(q) ||
      (a.employeeCode ?? '').toLocaleLowerCase('tr-TR').includes(q) ||
      (a.departmentName ?? '').toLocaleLowerCase('tr-TR').includes(q)
    );
  }, [available, searchLeft]);

  const filteredGroups = useMemo(() => {
    const q = searchRight.trim().toLocaleLowerCase('tr-TR');
    if (!q) return mealGroups;
    return mealGroups.map(g => ({
      ...g,
      employees: g.employees.filter(e =>
        `${e.firstName ?? ''} ${e.lastName ?? ''}`.toLocaleLowerCase('tr-TR').includes(q) ||
        (e.employeeCode ?? '').toLocaleLowerCase('tr-TR').includes(q)
      ),
    })).filter(g => g.employees.length > 0);
  }, [mealGroups, searchRight]);

  const toggleSelected = (id: number) => {
    setSelected(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };
  const selectAllVisible = () => setSelected(new Set(filteredAvailable.map(r => r.id)));
  const clearSelection = () => setSelected(new Set());

  const toggleDept = (name: string) => {
    setCollapsed(prev => {
      const s = new Set(prev);
      if (s.has(name)) s.delete(name); else s.add(name);
      return s;
    });
  };

  const moveToMeal = async () => {
    if (selected.size === 0) return;
    setBusy(true); setError(null); setFlash(null);
    try {
      await api.post('/meals/personnel/toggle', { EmployeeIds: Array.from(selected), IsMealPersonnel: true });
      setFlash(`${selected.size} personel yemekhane listesine eklendi`);
      setSelected(new Set());
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'İşlem başarısız');
    } finally { setBusy(false); setTimeout(() => setFlash(null), 3000); }
  };

  const removeFromMeal = async (id: number) => {
    if (!(await onayla('Bu personeli yemekhane listesinden çıkarmak istiyor musunuz?'))) return;
    setBusy(true); setError(null);
    try {
      await api.post('/meals/personnel/toggle', { EmployeeIds: [id], IsMealPersonnel: false });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'İşlem başarısız');
    } finally { setBusy(false); }
  };

  return (
    <div className="p-6 md:p-8 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-slate-800 flex items-center gap-2">
            <Users size={26} className="text-amber-600" /> Yemekhane Personeli
          </h1>
          <p className="text-sm text-slate-500 mt-1">Yemekhane geçiş hakkı olan personelleri yönet</p>
        </div>
        <button onClick={load} disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-xl text-sm font-bold text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-60">
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Yenile
        </button>
      </div>

      {error && <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>}
      {flash && <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-sm inline-flex items-center gap-2"><CheckCircle2 size={16} /> {flash}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sol: Tüm Personel */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-col">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="text-base font-black text-slate-800 flex items-center gap-2">
              <Users size={18} className="text-slate-500" /> Tüm Personel ({filteredAvailable.length})
            </h2>
            <div className="flex items-center gap-2">
              <button onClick={selectAllVisible} className="text-xs font-bold text-blue-600 hover:underline">Tümünü Seç</button>
              {selected.size > 0 && <button onClick={clearSelection} className="text-xs font-bold text-slate-500 hover:underline">Temizle</button>}
              <span className="text-xs font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded">{selected.size} seçili</span>
            </div>
          </div>
          <div className="flex items-center gap-2 mb-3">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input type="text" value={searchLeft} onChange={e => setSearchLeft(e.target.value)}
                placeholder="Ara..." className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none" />
            </div>
            <select value={depFilter} onChange={e => setDepFilter(e.target.value ? Number(e.target.value) : '')}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm">
              <option value="">Tüm Dept</option>
              {departmentOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          </div>
          <div className="flex-1 overflow-y-auto max-h-[520px] space-y-1">
            {filteredAvailable.length === 0 ? (
              <p className="text-center text-slate-400 text-sm py-16">Kayıt yok</p>
            ) : filteredAvailable.map(a => (
              <label key={a.id} className="flex items-center gap-2 px-2 py-2 rounded hover:bg-slate-50 cursor-pointer">
                <input type="checkbox" checked={selected.has(a.id)} onChange={() => toggleSelected(a.id)} className="w-4 h-4 rounded" />
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-slate-800 text-sm truncate">{a.firstName} {a.lastName}</p>
                  <p className="text-[11px] text-slate-500 truncate">#{a.employeeCode ?? '—'} · {a.departmentName ?? '—'}</p>
                </div>
              </label>
            ))}
          </div>
          <button onClick={moveToMeal} disabled={selected.size === 0 || busy}
            className="mt-3 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-600 text-white rounded-xl text-sm font-bold hover:bg-amber-700 disabled:opacity-40">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
            Seçilenleri Yemekhaneye Ekle
          </button>
        </div>

        {/* Sağ: Yemekhane Personeli */}
        <div className="bg-white border border-amber-300 border-l-4 rounded-2xl p-4 flex flex-col">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="text-base font-black text-slate-800 flex items-center gap-2">
              <ChefHat size={18} className="text-amber-600" /> Yemekhane Personeli ({mealTotal})
            </h2>
          </div>
          <div className="mb-3 relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input type="text" value={searchRight} onChange={e => setSearchRight(e.target.value)}
              placeholder="Ara..." className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 focus:outline-none" />
          </div>
          <div className="flex-1 overflow-y-auto max-h-[560px] space-y-2">
            {filteredGroups.length === 0 ? (
              <p className="text-center text-slate-400 text-sm py-16">
                <Utensils size={36} className="mx-auto opacity-30 mb-2" />
                Henüz yemekhane personeli yok
              </p>
            ) : filteredGroups.map(g => {
              const isCollapsed = collapsed.has(g.departmentName);
              return (
                <div key={g.departmentName} className="border border-slate-100 rounded-lg">
                  <button onClick={() => toggleDept(g.departmentName)}
                    className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 rounded-t-lg hover:bg-slate-100">
                    <span className="flex items-center gap-2 text-sm font-black text-slate-700">
                      {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                      {g.departmentName}
                    </span>
                    <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">{g.employees.length}</span>
                  </button>
                  {!isCollapsed && (
                    <ul className="divide-y divide-slate-100">
                      {g.employees.map(e => (
                        <li key={e.id} className="flex items-center justify-between px-3 py-2 hover:bg-slate-50 group">
                          <div className="min-w-0">
                            <p className="font-bold text-slate-800 text-sm truncate">{e.firstName} {e.lastName}</p>
                            <p className="text-[11px] text-slate-500 truncate">#{e.employeeCode ?? '—'} {e.cardNumber ? <span className="ml-2 px-1.5 py-0.5 bg-slate-100 rounded text-[10px] font-mono">{e.cardNumber}</span> : null}</p>
                          </div>
                          <button onClick={() => removeFromMeal(e.id)} disabled={busy}
                            className="opacity-0 group-hover:opacity-100 text-xs text-red-600 hover:underline font-bold inline-flex items-center gap-1">
                            <ArrowLeft size={12} /> Çıkar
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MealPersonnel;
